import { useBusinessContext } from "../../context/BusinessContext";
import { useTenant } from "../../hooks/useTenant";

/**
 * Public base URL guests open their pass on (shared by the confirmation view
 * and the invitations drawer).
 */
export const useInvitationBaseUrl = (): string => {
  const { business } = useBusinessContext();
  const { tenant } = useTenant();

  // Build the public-facing base URL for shareable invitation links.
  // Priority: tenant.domain (the canonical public website for this tenant) →
  // business.website_url (per-business override) → sessionStorage cache →
  // current admin domain with the "admin." subdomain stripped (last resort).
  // We always want the tenant's PUBLIC domain, never the admin host: copying
  // a link from `admin.business.capcana.app` would otherwise leak the admin
  // subdomain to guests.
  // SAM-150: the guest link must resolve in the SAME environment as the admin
  // that generated it. The tenant's `domain` is stored as the canonical PROD
  // hostname (e.g. "capcana.app"), so on the dev admin host
  // (dev.admin.business.capcana.app) the link pointed at prod and the guest app
  // returned "Invitation not found". Dev guest hosts follow the convention
  // `dev.<prod-domain>` (dev.capcana.app), so when we're running in a dev
  // environment we prefix the tenant domain with "dev." to match.
  const isDevEnvironment = (() => {
    const host = (typeof window !== 'undefined' ? window.location.host : '').toLowerCase();
    return (
      import.meta.env.DEV ||
      host.startsWith('dev.') ||
      host.includes('.dev.') ||
      host.includes('dev.admin')
    );
  })();

  const toEnvHost = (host: string) => {
    // Only add the dev marker; never strip it from a domain that already has it.
    if (isDevEnvironment && host && !/^dev\./i.test(host) && !host.includes('.dev.')) {
      return `dev.${host}`;
    }
    return host;
  };

  const getBaseUrl = () => {
    // 1. Canonical tenant domain — only stored as a hostname (e.g. "capcana.app"),
    //    so we have to add the protocol ourselves.
    const tenantDomain = (tenant?.domain || '').trim();
    if (tenantDomain) {
      const stripped = tenantDomain.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
      if (stripped) return `https://${toEnvHost(stripped)}`;
    }

    // 2. Per-business URL override (already a full URL by convention).
    if (business?.website_url) {
      return business.website_url.replace(/\/+$/, '');
    }

    // 3. Cached business data from a previous load.
    try {
      const businessData = sessionStorage.getItem('businessData');
      if (businessData) {
        const parsedData = JSON.parse(businessData);
        if (parsedData.website_url) {
          return parsedData.website_url.replace(/\/+$/, '');
        }
      }
    } catch (e) {
      console.error("Error parsing business data for website URL:", e);
    }

    // 4. Last resort: current admin host minus the "admin." subdomain.
    // Examples: dev.admin.domain.app → dev.domain.app, admin.domain.app → domain.app
    let currentDomain = `${window.location.protocol}//${window.location.host}`;
    currentDomain = currentDomain.replace(/\/\/admin\./, '//');
    currentDomain = currentDomain.replace(/\.admin\./, '.');
    return currentDomain;
  };

  return getBaseUrl();
};

/** Guest-facing pass link: `<base>/#/access/<code>` for business passes, `#/invitation/` otherwise. */
export const buildInvitationLink = (baseUrl: string, code: string | number | null | undefined, isBusiness: boolean): string => {
  if (code === null || code === undefined || code === '') return '';
  const cleanCode = String(code).replace(/^#\/(access|invitation)\//, '');
  return `${baseUrl.replace(/\/+$/, '')}/#/${isBusiness ? 'access' : 'invitation'}/${cleanCode}`;
};
